import { notify as defaultNotify } from "./toast";

export function getApiErrorMessage(error, fallback = "요청 처리 중 오류가 발생했습니다.") {
    const data = error?.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (error?.message) return error.message;
    return fallback;
}

export function assertApiResponse(response, fallback = "서버 응답이 올바르지 않습니다.") {
    if (response && typeof response !== "object") return response;

    if (response && !("status" in response) && !("data" in response)) {
        if (response?.success === false || response?.ok === false) {
            throw new Error(response?.message || response?.error || fallback);
        }
        return response;
    }

    const status = response?.status;
    if (status && (status < 200 || status >= 300)) {
        throw new Error(fallback);
    }

    const data = response?.data;
    if (data == null || data?.success === false || data?.ok === false) {
        throw new Error(data?.message || data?.error || fallback);
    }

    return data;
}

/**
 * @param {object} options
 * @param {() => Promise<any>} options.request
 * @param {string | ((serverMsg: string) => string)} [options.errorMsg]
 *   - string: 서버 메시지 무시하고 이 문자열을 toast에 표시
 *   - function: 서버 메시지를 받아 가공 후 표시
 *   - 미전달: 서버 에러 메시지 그대로 표시
 * @param {string | ((data: any) => string | { message: string, type: string })} [options.successMsg]
 *   - 미전달: 성공 toast 없음
 * @param {(data: any) => boolean | string} [options.validate]
 * @param {(loading: boolean) => void} [options.setLoading]
 * @param {(data: any) => void | Promise<void>} [options.onSuccess]
 * @param {(error: Error, message: string) => void | Promise<void>} [options.onError]
 * @param {() => void} [options.closeOnSuccess]
 * @param {boolean} [options.silent=false] - true면 toast 완전 비활성화
 * @param {boolean} [options.throwOnError=false]
 */
export async function runApiAction({
    request,
    errorMsg,
    successMsg,
    validate,
    setLoading,
    onSuccess,
    onError,
    closeOnSuccess,
    silent = false,
    throwOnError = false,
}) {
    const doNotify = silent ? () => {} : defaultNotify;

    try {
        setLoading?.(true);
        const data = await request();

        const validationResult = validate?.(data);
        if (typeof validationResult === "string") throw new Error(validationResult);
        if (validationResult === false) throw new Error("유효성 검사에 실패했습니다.");

        if (successMsg !== undefined) {
            const result = typeof successMsg === "function" ? successMsg(data) : successMsg;
            if (typeof result === "string") doNotify(result, "success");
            else if (result?.message) doNotify(result.message, result.type ?? "success");
        }

        await onSuccess?.(data);
        closeOnSuccess?.();
        return { ok: true, data };

    } catch (error) {
        const message = typeof errorMsg === "function"
            ? errorMsg(error.message)
            : (errorMsg ?? error.message ?? "요청 처리 중 오류가 발생했습니다.");
        doNotify(message, "error");
        await onError?.(error, message);
        if (throwOnError) throw error;
        return { ok: false, error, message };

    } finally {
        setLoading?.(false);
    }
}
